import { Injectable, inject } from '@angular/core';
import { UserModel } from '@portal/app/shared/models/userModel';
import { environment } from '@portal/environments/environment';
import { SelectionService } from '@portal/app/shared/services/selection.service';
import * as Sentry from '@sentry/angular';

@Injectable({
  providedIn: 'root'
})
export class SentryService {
  private selectionService = inject(SelectionService);

  public setupSentry(user: UserModel): void {
    if (!environment.production) {
      return;
    }

    const email = user.getEmail();
    if (!email) {
      console.warn('Sentry user setup aborted: Missing email address');
      return;
    }

    Sentry.setUser({
      email: email,
      username: user.getUsername()
    });

    Sentry.setTag('userType', user.getUserType());

    const { clientId, brandId, brandName } =
      this.selectionService.clientBrandSignal();
    Sentry.setTag('clientId', String(clientId));
    Sentry.setTag('brandId', String(brandId));
    Sentry.setTag('brandName', brandName);
  }

  public clearUser(): void {
    if (!environment.production) {
      return;
    }
    Sentry.setUser(null);
  }

  /**
   * Count - track events (clicks, API calls, orders, etc.)
   * @param name - Metric name (e.g., 'button_click', 'api_call')
   * @param value - Count value (default: 1)
   * @param attributes - Optional key-value pairs for filtering/grouping
   */
  public count(
    name: string,
    value = 1,
    attributes?: Record<string, string>
  ): void {
    if (!environment.production) {
      return;
    }
    Sentry.metrics.count(name, value, { attributes });
  }

  /**
   * Gauge - track current values (queue depth, active connections, etc.)
   * @param name - Metric name (e.g., 'active_users', 'queue_depth')
   * @param value - Current value
   * @param attributes - Optional key-value pairs for filtering/grouping
   */
  public gauge(
    name: string,
    value: number,
    attributes?: Record<string, string>
  ): void {
    if (!environment.production) {
      return;
    }
    Sentry.metrics.gauge(name, value, { attributes });
  }

  /**
   * Distribution - track value ranges (response times, payload sizes, etc.)
   * @param name - Metric name (e.g., 'api_response_time', 'file_size')
   * @param value - Measured value
   * @param unit - Optional unit (e.g., 'millisecond', 'byte')
   * @param attributes - Optional key-value pairs for filtering/grouping
   */
  public distribution(
    name: string,
    value: number,
    unit?: string,
    attributes?: Record<string, string>
  ): void {
    if (!environment.production) {
      return;
    }
    Sentry.metrics.distribution(name, value, { unit, attributes });
  }
}
